import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { Maybe } from '@oberoninternal/travelbase-ds/entities/Maybe';
import { UnreachableCaseError } from '@oberoninternal/travelbase-ds/entities/UnreachableCaseError';
import { Box, Flex } from '@rebass/grid';
import { gql } from '@apollo/client';
import { useRouter } from 'next/router';
import React, { FC } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import styled from 'styled-components';
import { useCheckoutDispatch } from '../context/checkout';
import { MyOrderFragment, OrderItemFragment } from '../generated/graphql';
import { not } from '../utils/fp';
import { getOrderTitle, isDeclined } from '../utils/order';
import { OrderItemProps, OrderItems } from './designsystem/OrderItem';
import OrderBooking from './OrderBooking';
import OrderTicket from './OrderTicket';
import Stack from './Stack';
import { Locale } from '../entities/Locale';

export const fragment = gql`
    fragment MyOrder on Order {
        id
        number
        createdDate
        paymentOptions {
            fullPaymentDueDate
            totalAmountDue
        }
        orderItems {
            ...OrderItem
        }
        payments {
            status
        }
    }

    fragment OrderItem on OrderItem {
        id
        ...OrderBooking
        ...OrderTicket
        ...OrderSummaryItem
    }
`;

interface Props {
    data: MyOrderFragment;
}

const Order: FC<React.PropsWithChildren<Props>> = ({ data }) => {
    const { orderItems, paymentOptions, createdDate, id } = data;
    const { query, ...router } = useRouter();
    const { init } = useCheckoutDispatch();
    const intl = useIntl();
    return (
        <Box>
            <Flex justifyContent="space-between" pb={4} alignItems="center">
                <Box>
                    <StyledTitle variant="small">{getOrderTitle(data, router.locale as Locale)}</StyledTitle>
                    <Body>
                        {createdDate && (
                            <FormattedMessage
                                defaultMessage="Gemaakt op {date}"
                                values={{
                                    date: <FormattedDate format="DAYMONTHYEAR" value={new Date(createdDate)} />,
                                }}
                            />
                        )}
                    </Body>
                </Box>

                {orderItems.every(not(isDeclined)) && paymentOptions && paymentOptions.totalAmountDue > 0 && (
                    <Stack className="gt-s" spacing={4} variant="inline" alignItems="center" mr={[null, null, null, 7]}>
                        <Body variant="tiny" style={{ fontWeight: 'normal' }}>
                            <FormattedMessage
                                defaultMessage="Nog te voldoen {price}"
                                values={{
                                    price: (
                                        <strong style={{ fontWeight: 700 }}>
                                            <FormattedNumber value={paymentOptions.totalAmountDue} format="EUR" />
                                        </strong>
                                    ),
                                }}
                            />
                        </Body>

                        <Button
                            onClick={() => {
                                init('order');
                                router.push(`/my/order/${id}/checkout/payment`);
                            }}
                        >
                            <FormattedMessage defaultMessage="Restant betalen" />
                        </Button>
                    </Stack>
                )}
            </Flex>
            {id && (
                <OrderItems>
                    {orderItems.map((item, i) => (
                        <Item
                            key={i}
                            item={item}
                            orderId={id}
                            linkProps={{
                                href: `/my/order/${id}`,
                                locale: intl.locale,
                            }}
                        />
                    ))}
                </OrderItems>
            )}
        </Box>
    );
};

export default Order;

interface ItemProps extends OrderItemProps {
    item: OrderItemFragment;
    orderId: Maybe<string>;
}

export const Item = ({ item, orderId, ...props }: ItemProps) => {
    if (!item.__typename) {
        return null;
    }
    switch (item.__typename) {
        case 'Booking':
            return <OrderBooking data={item} orderId={orderId} {...props} />;
        case 'Ticket':
            return <OrderTicket data={item} {...props} />;
        // TODO: implement UpsellPurchase
        case 'UpsellPurchase':
            return null;
        default:
            throw new UnreachableCaseError(item.__typename);
    }
};

const StyledTitle = styled(Title)`
    a {
        font: inherit;
        font-weight: 400;
    }
`;
